import { supabase, Campaña } from '../lib/supabase';
import { getLumisialesFiltrados, extraerDatosJerarquia, FiltrosAdminBase } from './adminServiceHelpers';

export interface FiltrosAdminCampanas extends FiltrosAdminBase {
  estado?: 'planificacion' | 'en_curso' | 'finalizada' | 'archivada';
  busqueda?: string;
}

export interface LumisialCampanasGroup {
  lumisialId: string;
  lumisialNombre: string;
  lumisialCiudad: string;
  totalCampanas: number;
  totalObjetivo: number;
  totalRecaudado: number;
  campanas: Campaña[];
}

export interface DiocesisCampanasGroup {
  diocesisId: string;
  diocesisNombre: string;
  diocesisTipo: 'diocesis' | 'prefectura';
  totalCampanas: number;
  totalObjetivo: number;
  totalRecaudado: number;
  lumisiales: LumisialCampanasGroup[];
}

export interface ProvinciaCampanasGroup {
  provinciaId: string;
  provinciaNombre: string;
  totalCampanas: number;
  totalObjetivo: number;
  totalRecaudado: number;
  diocesis: DiocesisCampanasGroup[];
}

export interface HierarchicalCampanasStats {
  provincias: ProvinciaCampanasGroup[];
}

class CampanaAdminService {
  async getCampanasGrouped(filtros: FiltrosAdminCampanas): Promise<HierarchicalCampanasStats> {
    try {
      const lumisiales = await getLumisialesFiltrados(filtros);
      if (!lumisiales || lumisiales.length === 0) return { provincias: [] };

      const lumisialIds = lumisiales.map(l => l.id);

      let campanasQuery = supabase
        .from('campañas')
        .select('*')
        .in('lumisial_id', lumisialIds)
        .order('fecha_inicio', { ascending: false });

      if (filtros.estado) {
        campanasQuery = campanasQuery.eq('estado', filtros.estado);
      } else {
        campanasQuery = campanasQuery.neq('estado', 'archivada');
      }

      if (filtros.busqueda) {
        campanasQuery = campanasQuery.ilike('nombre', `%${filtros.busqueda}%`);
      }

      const { data: campanas, error: campanasError } = await campanasQuery;
      if (campanasError) throw campanasError;

      const campanasPorLumisial = new Map<string, Campaña[]>();
      for (const c of (campanas || [])) {
        const grupo = campanasPorLumisial.get(c.lumisial_id) || [];
        grupo.push(c);
        campanasPorLumisial.set(c.lumisial_id, grupo);
      }

      const provinciasMap = new Map<string, ProvinciaCampanasGroup>();

      for (const lumisial of lumisiales) {
        const cans = campanasPorLumisial.get(lumisial.id) || [];

        const totalObjetivo = cans.reduce((s, c) => s + Number(c.objetivo_recaudacion || 0), 0);
        const totalRecaudado = cans.reduce((s, c) => s + Number(c.monto_recaudado || 0), 0);

        const lumisialGroup: LumisialCampanasGroup = {
          lumisialId: lumisial.id,
          lumisialNombre: lumisial.nombre,
          lumisialCiudad: lumisial.ciudad || '',
          totalCampanas: cans.length,
          totalObjetivo,
          totalRecaudado,
          campanas: cans,
        };

        const { provinciaId, provinciaNombre, diocesisId, diocesisNombre, diocesisTipo } = extraerDatosJerarquia(lumisial);

        if (!provinciasMap.has(provinciaId)) {
          provinciasMap.set(provinciaId, {
            provinciaId,
            provinciaNombre,
            totalCampanas: 0,
            totalObjetivo: 0,
            totalRecaudado: 0,
            diocesis: [],
          });
        }

        const provincia = provinciasMap.get(provinciaId)!;
        let diocesisGroup = provincia.diocesis.find(d => d.diocesisId === diocesisId);

        if (!diocesisGroup) {
          diocesisGroup = {
            diocesisId,
            diocesisNombre,
            diocesisTipo,
            totalCampanas: 0,
            totalObjetivo: 0,
            totalRecaudado: 0,
            lumisiales: [],
          };
          provincia.diocesis.push(diocesisGroup);
        }

        diocesisGroup.lumisiales.push(lumisialGroup);
        diocesisGroup.totalCampanas += lumisialGroup.totalCampanas;
        diocesisGroup.totalObjetivo += lumisialGroup.totalObjetivo;
        diocesisGroup.totalRecaudado += lumisialGroup.totalRecaudado;

        provincia.totalCampanas += lumisialGroup.totalCampanas;
        provincia.totalObjetivo += lumisialGroup.totalObjetivo;
        provincia.totalRecaudado += lumisialGroup.totalRecaudado;
      }

      return { provincias: Array.from(provinciasMap.values()) };
    } catch (error) {
      console.error('Error al obtener campañas agrupadas:', error);
      throw error;
    }
  }
}

export const campanaAdminService = new CampanaAdminService();
